var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { setUserId, trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import { IDENTIFIED_USER_SESSION_NAME, SNOWPLOW } from './constants';
import { getBrowsingApp, getCountryPlatform, initExponea, initSnowplow, isCustomerLoggedIn, } from './utils';
let launchDarklyClient;
export const initializeTracking = (locale, enableExponea = true, appId, ldClient) => {
    window.sessionStorage.removeItem(IDENTIFIED_USER_SESSION_NAME);
    initSnowplow(locale, appId);
    if (enableExponea) {
        initExponea(locale);
    }
    launchDarklyClient = ldClient;
};
export const isExponeaInitialized = () => {
    var _a;
    return typeof global !== 'undefined' && !!((_a = global.window) === null || _a === void 0 ? void 0 : _a['exponea']);
};
export const trackEvent = (_a, trackToExponea, trackToSnowplow, targetURL, event, openInNewTab) => {
    var { schema } = _a, trackingProperties = __rest(_a, ["schema"]);
    if (trackToExponea === void 0) { trackToExponea = false; }
    if (trackToSnowplow === void 0) { trackToSnowplow = true; }
    if (targetURL === void 0) { targetURL = undefined; }
    if (event === void 0) { event = undefined; }
    if (openInNewTab === void 0) { openInNewTab = false; }
    const promises = [];
    const trackingData = mapTrackingData(trackingProperties);
    if (event) {
        event.preventDefault();
    }
    if (process.env.NODE_ENV === 'development') {
        console.debug('Tracking event emitted', trackingProperties);
    }
    if (trackToExponea) {
        promises.push(trackEventToExponea({ trackingData }));
    }
    if (trackToSnowplow) {
        promises.push(trackEventToSnowplow({ schema, trackingData }));
    }
    /*
     * NOTE: This is specific for customer-portal
     * other portals such as CAP or Raisin frontend might not work
     * send Snowplow events to Launch Darkly
     * only if the user has given consent (UserCentrics)
     */
    // temporarily comment out the LD consent check
    promises.push(trackEventToLaunchDarkly({ trackingData }));
    return Promise.allSettled(promises).then((results) => {
        const rejections = results
            .map((result) => (result.status === 'rejected' ? result.reason : null))
            .filter(Boolean);
        if (targetURL) {
            if (openInNewTab) {
                window.open(targetURL, '_blank');
            }
            else {
                window.location.href = targetURL;
            }
        }
        if (rejections.length) {
            console.warn('One or more tracking events failed', ...rejections);
        }
    });
};
const mapTrackingData = ({ eventName, object, action, portal, description, boundedContext, zone, bipsDomain, context, component, productId, customerGroup, customAttribute1, customAttribute2, customAttribute3, customAttribute4, customAttribute5, customAttribute6, customAttribute7, customAttribute8, customAttribute9, customAttribute10, countryPlatform, }) => {
    return {
        event_name: eventName,
        object,
        action,
        portal,
        description,
        bounded_context: boundedContext,
        zone,
        context,
        component,
        product_id: productId,
        bips_domain: bipsDomain,
        browsing_platform: getBrowsingApp(),
        country_platform: countryPlatform || getCountryPlatform(),
        customer_group: customerGroup,
        custom_attribute_1: customAttribute1,
        custom_attribute_2: customAttribute2,
        custom_attribute_3: customAttribute3,
        custom_attribute_4: customAttribute4,
        custom_attribute_5: customAttribute5,
        custom_attribute_6: customAttribute6,
        custom_attribute_7: customAttribute7,
        custom_attribute_8: customAttribute8,
        custom_attribute_9: customAttribute9,
        custom_attribute_10: customAttribute10,
    };
};
const trackEventToSnowplow = ({ schema, trackingData, }) => {
    return new Promise((resolve) => {
        const isLoggedIn = isCustomerLoggedIn();
        const event = {
            event: {
                schema: schema || SNOWPLOW.SCHEMA,
                data: trackingData,
            },
        };
        if (isLoggedIn) {
            let counter = 0;
            const interval = setInterval(() => {
                const isUserIdentified = !!window.sessionStorage.getItem(IDENTIFIED_USER_SESSION_NAME);
                if (isUserIdentified) {
                    trackSelfDescribingEvent(event);
                    clearInterval(interval);
                    resolve();
                }
                if (++counter > 10) {
                    clearInterval(interval);
                    resolve();
                }
            }, 300);
        }
        else {
            trackSelfDescribingEvent(event);
            resolve();
        }
    });
};
const trackEventToExponea = ({ trackingData }) => {
    return new Promise((resolve, reject) => {
        const successCallback = () => {
            resolve();
        };
        const errorCallback = () => {
            reject('Exponea failed to fire a tracking event');
        };
        const exponeaObject = window === null || window === void 0 ? void 0 : window['exponea'];
        if (exponeaObject === null || exponeaObject === void 0 ? void 0 : exponeaObject.isInitialized) {
            exponeaObject.track(trackingData.event_name.toLowerCase(), trackingData, () => successCallback(), () => errorCallback());
            return;
        }
        else {
            reject('Exponea is not initialized!');
        }
    });
};
const trackEventToLaunchDarkly = ({ trackingData, }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!launchDarklyClient) {
        console.warn('LaunchDarkly client not initialized!');
        return;
    }
    try {
        launchDarklyClient.track(trackingData.event_name, { customProperty: trackingData });
    }
    catch (error) {
        console.warn('LaunchDarkly tracking failed:', error);
    }
});
export const identifyUserBy = (type, value) => {
    const exponeaObject = window === null || window === void 0 ? void 0 : window['exponea'];
    if (exponeaObject === null || exponeaObject === void 0 ? void 0 : exponeaObject.isInitialized) {
        exponeaObject.identify({ [type]: value });
    }
    setUserId === null || setUserId === void 0 ? void 0 : setUserId(value);
    window.sessionStorage.setItem(IDENTIFIED_USER_SESSION_NAME, 'true');
};
//# sourceMappingURL=index.js.map